// Real Professional Data for Berkant Bıçakçı - AI Engineer Portfolio

export const profileData = {
  name: "Berkant Bıçakçı",
  title: "AI Engineer / Data Analyst",
  location: "Istanbul, Turkey",
  tagline: "Building data-driven AI systems and scalable machine learning solutions",
  bio: "AI Engineer with hands-on experience in machine learning, LLM-based systems, and data-intensive modeling. Specialized in developing production-ready AI pipelines, from data preprocessing to model deployment. Strong background in applied research, deep learning, and cloud-based ML infrastructure.",
  email: "info@berkantbicakci.com",
  linkedin: "https://linkedin.com/in/berkantbb",
  github: "https://github.com/BerkantBicakci",
  cvUrl: "/assets/Berkant_Bicakci_CV.pdf"
};

export const services = [
  {
    id: 1,
    title: "Machine Learning Solutions",
    description: "Design and implement custom ML models for classification, regression, and computer vision tasks. From data preprocessing to model deployment, I deliver end-to-end solutions.",
    features: [
      "Deep Learning (CNN, YOLO, PyTorch)",
      "Model Training & Optimization",
      "Computer Vision Applications",
      "Production-Ready ML Pipelines"
    ],
    icon: "brain",
    cta: "Get Started"
  },
  {
    id: 2,
    title: "LLM-Based Systems",
    description: "Build intelligent systems powered by Large Language Models. Specialized in knowledge graphs, semantic search, and LLM-based agent architectures for real-world applications.",
    features: [
      "LLM Integration & Fine-tuning",
      "Knowledge Graph Development",
      "Semantic Search Systems",
      "Multi-Agent LLM Architectures"
    ],
    icon: "sparkles",
    cta: "Work With Me"
  },
  {
    id: 3,
    title: "Data Engineering & Analysis",
    description: "Transform raw data into actionable insights. Expert in data preprocessing, feature engineering, fuzzy matching, and building scalable data pipelines.",
    features: [
      "Data Preprocessing & Cleaning",
      "ETL Pipeline Development",
      "Fuzzy Matching & Deduplication",
      "SQL & Database Optimization"
    ],
    icon: "database",
    cta: "Learn More"
  },
  {
    id: 4,
    title: "Cloud AI Infrastructure",
    description: "Deploy and scale AI solutions on cloud platforms. Experience with Azure and AWS for building robust, production-grade ML infrastructure.",
    features: [
      "Azure ML Services",
      "Cloud-Based Model Deployment",
      "Scalable AI Pipelines",
      "MLOps Best Practices"
    ],
    icon: "cloud",
    cta: "Start Project"
  }
];

export const skills = [
  { name: "Python", icon: "code-2", category: "Programming" },
  { name: "Machine Learning", icon: "brain", category: "AI/ML" },
  { name: "Deep Learning", icon: "network", category: "AI/ML" },
  { name: "LLM & NLP", icon: "sparkles", category: "AI/ML" },
  { name: "PyTorch", icon: "flame", category: "Frameworks" },
  { name: "SQL", icon: "database", category: "Data" },
  { name: "Data Preprocessing", icon: "filter", category: "Data" },
  { name: "Azure / AWS", icon: "cloud", category: "Cloud" },
  { name: "Model Evaluation", icon: "bar-chart-3", category: "ML Ops" }
];

export const projects = [
  {
    id: 1,
    title: "LLM-Based Knowledge Graph System at Arcelik",
    problem: "Arcelik needed an intelligent system to structure and query large-scale product and technical documentation. Traditional search methods couldn't capture semantic relationships or provide contextual answers from unstructured data.",
    approach: [
      "Designed and implemented an LLM-powered knowledge graph system to extract entities and relationships from technical documents",
      "Developed advanced data preprocessing pipeline with fuzzy matching algorithms for entity resolution and deduplication",
      "Built semantic search capabilities using embedding models for contextual information retrieval",
      "Integrated the system with existing databases and implemented evaluation metrics for precision and recall"
    ],
    technologies: ["Python", "LangChain", "OpenAI API", "Neo4j", "Fuzzy Matching", "Embeddings", "FastAPI"],
    results: {
      metrics: [
        { label: "Precision", value: "83%" },
        { label: "Recall", value: "94%" },
        { label: "Query Response", value: "< 2s" }
      ],
      impact: "Successfully deployed knowledge graph system handling thousands of technical documents. Improved information retrieval accuracy by 83% precision and 94% recall, enabling faster decision-making for engineering teams."
    }
  },
  {
    id: 2,
    title: "Deepfake & Liveness Detection System",
    problem: "Growing security concerns around deepfake videos and presentation attacks required a robust authentication system. Traditional face recognition alone was insufficient to detect sophisticated spoofing attempts.",
    approach: [
      "Developed a multi-modal detection system combining deepfake detection and liveness verification",
      "Trained deep learning models (CNNs) to identify artifacts in manipulated videos and images",
      "Implemented real-time liveness detection using behavioral biometrics and texture analysis",
      "Built end-to-end ML pipeline from data collection to model deployment with continuous monitoring"
    ],
    technologies: ["Python", "PyTorch", "OpenCV", "CNNs", "Computer Vision", "Docker", "Azure ML"],
    results: {
      metrics: [
        { label: "Detection Accuracy", value: "92.5%" },
        { label: "False Positive Rate", value: "< 3%" },
        { label: "Real-time Processing", value: "30 FPS" }
      ],
      impact: "Deployed production-ready authentication system preventing spoofing attacks. Achieved 92.5% accuracy in detecting deepfakes while maintaining low false positive rates, enhancing security for critical applications."
    }
  },
  {
    id: 3,
    title: "Deep Learning Waste Detection System",
    problem: "Manual waste sorting is inefficient, costly, and environmentally unsustainable. Automated systems needed to accurately classify different types of waste materials for recycling optimization.",
    approach: [
      "Built object detection model using CNN and YOLO architectures for real-time waste classification",
      "Collected and annotated custom dataset of various waste categories (plastic, metal, paper, organic)",
      "Implemented data augmentation and transfer learning to improve model generalization",
      "Optimized model for edge deployment with quantization and pruning techniques"
    ],
    technologies: ["Python", "PyTorch", "YOLO", "OpenCV", "Transfer Learning", "TensorRT"],
    results: {
      metrics: [
        { label: "Classification Accuracy", value: "89.7%" },
        { label: "Inference Speed", value: "45 FPS" },
        { label: "Categories Detected", value: "12" }
      ],
      impact: "Developed efficient waste detection system capable of processing real-time video feeds. Model achieved 89.7% accuracy across 12 waste categories, enabling automated sorting for recycling facilities."
    }
  },
  {
    id: 4,
    title: "Quantum Route Optimization with QAOA",
    problem: "Classical algorithms struggle with complex vehicle routing problems at scale. Quantum computing offers potential for solving combinatorial optimization problems more efficiently.",
    approach: [
      "Implemented Quantum Approximate Optimization Algorithm (QAOA) for route optimization problems",
      "Formulated vehicle routing problem as QUBO (Quadratic Unconstrained Binary Optimization)",
      "Compared quantum approach with classical optimization methods (genetic algorithms, simulated annealing)",
      "Evaluated performance on quantum simulators and explored hybrid quantum-classical approaches"
    ],
    technologies: ["Python", "Qiskit", "QAOA", "NetworkX", "Optimization Algorithms", "NumPy"],
    results: {
      metrics: [
        { label: "Solution Quality", value: "95% optimal" },
        { label: "Computation Time", value: "60% faster" },
        { label: "Scalability", value: "20+ nodes" }
      ],
      impact: "Successfully applied quantum computing to real-world optimization problem. QAOA achieved 95% near-optimal solutions with 60% faster computation compared to classical methods for moderate-sized problems."
    }
  }
];

export const certifications = [
  {
    title: "Microsoft Azure Fundamentals (AZ-900)",
    issuer: "Microsoft",
    year: "2024"
  },
  {
    title: "Python for Data Science",
    issuer: "Miuul",
    year: "2024"
  }
];

export const experience = [
  {
    company: "Arcelik A.S.",
    role: "AI Engineer",
    period: "Current",
    location: "Istanbul, Turkey",
    achievements: [
      "Developed LLM-based knowledge graph system for structuring and querying technical documentation, achieving 83% precision and 94% recall",
      "Built advanced data preprocessing pipelines with fuzzy matching algorithms for entity resolution across large-scale datasets",
      "Implemented semantic search capabilities using embedding models for contextual information retrieval",
      "Collaborated with cross-functional teams to integrate AI solutions into production systems"
    ]
  },
  {
    company: "Arcelik A.S.",
    role: "AI Intern",
    period: "2024",
    location: "Istanbul, Turkey",
    achievements: [
      "Designed and developed deepfake detection and liveness verification system for secure authentication",
      "Trained deep learning models (CNNs) achieving 92.5% accuracy in identifying manipulated media",
      "Built end-to-end ML pipeline from data collection to model deployment with performance monitoring",
      "Implemented real-time processing capabilities handling 30 FPS for production deployment"
    ]
  },
  {
    company: "ConStraight Cloud Consulting",
    role: "Junior Cloud Engineer",
    period: "2023",
    location: "Istanbul, Turkey",
    achievements: [
      "Deployed and managed ML models on Microsoft Azure cloud infrastructure",
      "Developed scalable AI pipelines using Azure ML Services for production workloads",
      "Implemented MLOps best practices for model versioning, monitoring, and automated retraining",
      "Collaborated on cloud architecture design for data-intensive AI applications"
    ]
  },
  {
    company: "ArVis Technology",
    role: "AI Deeptech R&D Intern",
    period: "2023",
    location: "Istanbul, Turkey",
    achievements: [
      "Researched and implemented quantum computing algorithms (QAOA) for route optimization problems",
      "Developed deep learning models for waste detection using CNN and YOLO architectures",
      "Achieved 89.7% accuracy in real-time waste classification across 12 categories",
      "Optimized models for edge deployment with quantization techniques"
    ]
  },
  {
    company: "AYVOS Technology",
    role: "AI R&D Intern",
    period: "2023",
    location: "Istanbul, Turkey",
    achievements: [
      "Developed multi-agent reinforcement learning systems for collaborative decision-making",
      "Implemented custom training environments and reward functions for agent optimization",
      "Conducted experiments comparing different RL algorithms and architectures",
      "Documented research findings and presented results to technical team"
    ]
  }
];

export const education = [
  {
    degree: "B.S. in Mathematical Engineering",
    school: "Yildiz Technical University",
    period: "2020 - 2025",
    location: "Istanbul, Turkey",
    relevant: [
      "Machine Learning & Deep Learning",
      "Optimization Theory",
      "Probability & Statistics",
      "Applied Mathematics",
      "Computational Methods"
    ]
  }
];
